<template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">monitor_heart</span>
      <p>
        Registra y evalúa las constantes vitales del paciente durante la atención:
        temperatura, presión arterial, pulso, peso, talla, IMC y más. Cada métrica
        muestra automáticamente una interpretación clínica del valor ingresado.
      </p>
    </div>

    <!-- Métricas disponibles -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">straighten</span>
        Métricas registradas
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="m in metricas" :key="m.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ m.icon }}</span>
          <div>
            <p class="help-card-title">{{ m.titulo }}</p>
            <p class="help-card-desc">{{ m.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Interpretación automática -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">analytics</span>
        Interpretación clínica automática
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#d4edda;color:#155724">Normal</span>
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">Alerta / Leve</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Crítico / Urgente</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        Al ingresar un valor, el sistema muestra automáticamente la interpretación
        clínica en color <strong style="color:green">verde</strong> (normal),
        <strong style="color:orange">naranja</strong> (alerta) o
        <strong style="color:red">rojo</strong> (crítico), según los rangos
        establecidos para cada métrica.
      </p>
    </section>

    <!-- Presión arterial -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">favorite</span>
        Presión arterial
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem">
        La presión arterial requiere dos valores separados:
        <strong>Sistólica</strong> (presión máxima) y
        <strong>Diastólica</strong> (presión mínima). El sistema los guarda
        de forma independiente y muestra el resultado combinado como
        <strong>Sistólica / Diastólica</strong> en la tabla.
      </p>
    </section>

    <!-- IMC -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">calculate</span>
        Cálculo de IMC
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem">
        El Índice de Masa Corporal se calcula automáticamente a partir del
        <strong>peso</strong> y la <strong>talla</strong> ya registrados.
        Pulsa el botón <strong>Calcular</strong> en el modal del IMC para
        obtener el resultado. El valor se completa solo y no requiere ingreso manual.
      </p>
    </section>

    <!-- Curvas de crecimiento -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">show_chart</span>
        Curvas de crecimiento
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem">
        El botón <strong>Curvas de Crecimiento</strong> aparece únicamente
        cuando el paciente tiene <strong>5 años o menos</strong>. Abre una
        vista con las curvas de la OMS para evaluar el desarrollo del niño
        según peso, talla y perímetro cefálico.
      </p>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Las constantes vitales solo pueden editarse cuando la atención está en
        estado <strong>PENDIENTE</strong> o <strong>PROCESO</strong>. En otros
        estados la columna de edición no se muestra.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaBiomAten",
  data() {
    return {
      metricas: [
        { icon: "thermostat",      titulo: "Temperatura",              desc: "En grados Celsius. Rango normal: 35.0 – 37.5 °C." },
        { icon: "favorite",        titulo: "Presión arterial",         desc: "Sistólica y diastólica en mmHg. Requiere dos valores. Normal: < 120 / < 80 mmHg." },
        { icon: "pulse_alert",     titulo: "Pulso",                    desc: "Frecuencia cardíaca en latidos por minuto. Normal adulto: 60 – 100 lpm." },
        { icon: "air",             titulo: "Frecuencia respiratoria",  desc: "Respiraciones por minuto. Normal adulto: 12 – 20 rpm." },
        { icon: "scale",           titulo: "Peso",                     desc: "En kilogramos. Se usa junto a la talla para calcular el IMC." },
        { icon: "height",          titulo: "Talla",                    desc: "En centímetros. Se usa junto al peso para calcular el IMC." },
        { icon: "calculate",       titulo: "IMC",                      desc: "Índice de Masa Corporal. Se calcula automáticamente con el botón Calcular. Normal: 18.5 – 24.9." },
        { icon: "straighten",      titulo: "Perímetro abdominal",      desc: "En centímetros. Los rangos de riesgo varían según el sexo del paciente." },
        { icon: "water_drop",      titulo: "Hemoglobina capilar",      desc: "En g/dL. Los rangos normales varían según el sexo del paciente." },
        { icon: "glucose",         titulo: "Glucosa capilar",          desc: "En mg/dL. Normal en ayunas: 70 – 99 mg/dL." },
        { icon: "monitor_heart",   titulo: "Pulsioximetría",           desc: "Saturación de oxígeno en %. Normal: ≥ 95%." },
        { icon: "child_care",      titulo: "Perímetro cefálico",       desc: "En centímetros. Aplicable principalmente en recién nacidos y lactantes." },
      ],
      acciones: [
        {
          icon:   "edit",
          color:  "#198754",
          titulo: "Editar constante vital",
          desc:   "Abre el modal para ingresar o actualizar el valor de la métrica seleccionada. Muestra la interpretación clínica en tiempo real. Solo disponible en atenciones PENDIENTE o PROCESO.",
        },
        {
          icon:   "calculate",
          color:  "#0059cc",
          titulo: "Calcular IMC",
          desc:   "Visible solo al editar la métrica IMC. Envía los datos al servidor para calcular el IMC a partir del peso y talla registrados y rellena el valor automáticamente.",
        },
        {
          icon:   "show_chart",
          color:  "#6f42c1",
          titulo: "Curvas de crecimiento",
          desc:   "Visible solo en pacientes de 5 años o menos. Navega a la vista de curvas de crecimiento de la OMS para evaluar el desarrollo del niño.",
        },
        {
          icon:   "refresh",
          color:  "#6c757d",
          titulo: "Recargar",
          desc:   "Recarga la lista de constantes vitales registradas para la atención actual.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>